import { useQuery, useSubscription } from "@apollo/client/react";
import { useCallback, useEffect, useState } from "react";

import {
  FUER_DICH_CHANGED,
  FUER_DICH_OPEN_COUNT,
} from "@/components/notifications/notificationQueries";

/**
 * The number of the caller's open "Für dich" entries (#807), kept live.
 *
 * The push (`notificationsChanged`) is only a signal and pubsub is at-most-once, so the
 * query is the truth and three things refetch it: every push, a 60 s poll, and the window
 * becoming visible again (heals pushes missed while the socket was down or the tab slept).
 *
 * `count` is null until the first answer. A failed refetch keeps the last known count and
 * shows nothing: the poll and the next push retry.
 *
 * `changeTick` goes up on every push and whenever a later answer carries a different count
 * (poll, focus or push). The open list reads it as "something may have changed" and checks
 * in the background; it never rewrites its rows from it.
 */
export function useFuerDichCount(enabled: boolean): {
  count: number | null;
  changeTick: number;
  refetch: () => void;
} {
  const [pushes, setPushes] = useState(0);
  const [seen, setSeen] = useState<{ count: number | null; changes: number }>({
    count: null,
    changes: 0,
  });
  const {
    data,
    previousData,
    refetch: refetchQuery,
  } = useQuery(FUER_DICH_OPEN_COUNT, {
    skip: !enabled,
    pollInterval: 60_000,
    fetchPolicy: "cache-and-network",
  });

  const refetch = useCallback(() => {
    // A failed refetch is not an error to show: the bell keeps its last count and the next
    // poll or push retries.
    refetchQuery().catch(() => undefined);
  }, [refetchQuery]);

  useSubscription(FUER_DICH_CHANGED, {
    skip: !enabled,
    onData: () => {
      setPushes((n) => n + 1);
      refetch();
    },
    onError: (err) =>
      console.warn("notificationsChanged subscription error", err),
  });

  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") refetch();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [enabled, refetch]);

  const count = (data ?? previousData)?.notificationOpenCount ?? null;
  // Previous-value state, set while rendering (not in an effect): the first answer is the
  // baseline, every different one after it is a change.
  if (count !== null && count !== seen.count) {
    setSeen({
      count,
      changes: seen.count === null ? seen.changes : seen.changes + 1,
    });
  }
  return { count, changeTick: pushes + seen.changes, refetch };
}
